import React, { useState } from 'react';
import { Toaster } from 'react-hot-toast';
import { AuthProvider, useAuth } from './context/AuthContext';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import Leads from './pages/Leads';
import LeadDetail from './pages/LeadDetail';
import EnrolledClients from './pages/EnrolledClients';
import ReportsPage from './pages/ReportsPage';
import SettingsPage from './pages/SettingsPage';
import RetainerPage from './pages/RetainerPage';
import { PaymentsPage, ApprovalsPage, FollowUpsPage } from './pages/OtherPages';
import Sidebar from './components/Sidebar';
import NotificationBell from './components/NotificationBell';

const PAGE_ROLES = {
  dashboard:  ['admin','tl','consultant'],
  leads:      ['admin','tl','consultant'],
  clients:    ['admin','tl','consultant'],
  followups:  ['admin','tl','consultant'],
  payments:   ['admin','tl','consultant'],
  retainer:   ['admin','tl'],
  approvals:  ['admin','tl'],
  reports:    ['admin','tl'],
  settings:   ['admin'],
};

function hasAccess(role, page) {
  return PAGE_ROLES[page]?.includes(role) ?? false;
}

function CRMApp() {
  const { user, logout } = useAuth();
  const [currentPage, setCurrentPage] = useState('dashboard');
  const [selectedLead, setSelectedLead] = useState(null);

  if (!user) return <Login />;

  const navigate = (page) => {
    setSelectedLead(null);
    setCurrentPage(page);
  };

  if (selectedLead) {
    return (
      <div className="app-layout">
        <Sidebar currentPage={currentPage} onNavigate={navigate} />
        <div className="main-content">
          <div className="topbar">
            <button className="btn btn-ghost btn-sm" onClick={() => setSelectedLead(null)}>← Back</button>
            <div className="topbar-right">
              <NotificationBell />
              <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                {new Date().toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <div style={{ width: 28, height: 28, borderRadius: '50%', background: 'var(--navy)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--gold-light)', fontWeight: 700, fontSize: 12 }}>
                  {user.name?.[0]}
                </div>
                <span style={{ fontSize: 13, fontWeight: 600 }}>{user.name}</span>
                <span className="badge badge-navy" style={{ fontSize: 10 }}>{user.role}</span>
              </div>
              <button className="btn btn-ghost btn-sm" onClick={logout}>Logout</button>
            </div>
          </div>
          <LeadDetail leadId={selectedLead} onBack={() => setSelectedLead(null)} onNavigate={navigate} />
        </div>
      </div>
    );
  }

  const PAGE_TITLES = {
    dashboard: 'Dashboard', leads: 'Leads Pipeline', clients: 'Enrolled Clients',
    followups: 'Follow-ups', payments: 'Payments', retainer: 'Retainer Billing',
    approvals: 'Approvals', reports: 'Reports', settings: 'Settings',
  };

  const PAGE_MAP = {
    dashboard:  <Dashboard onNavigate={navigate} />,
    leads:      <Leads onSelectLead={setSelectedLead} />,
    clients:    <EnrolledClients onSelectLead={setSelectedLead} />,
    followups:  <FollowUpsPage onSelectLead={setSelectedLead} />,
    payments:   <PaymentsPage />,
    retainer:   <RetainerPage />,
    approvals:  <ApprovalsPage />,
    reports:    <ReportsPage />,
    settings:   <SettingsPage />,
  };

  const safePage = hasAccess(user.role, currentPage) ? currentPage : 'dashboard';

  return (
    <div className="app-layout">
      <Sidebar currentPage={safePage} onNavigate={navigate} />
      <div className="main-content">
        <div className="topbar">
          <h1 className="topbar-title">{PAGE_TITLES[safePage]}</h1>
          <div className="topbar-right">
            <NotificationBell />
            <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>
              {new Date().toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <div style={{ width: 28, height: 28, borderRadius: '50%', background: 'var(--navy)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--gold-light)', fontWeight: 700, fontSize: 12 }}>
                {user.name?.[0]}
              </div>
              <span style={{ fontSize: 13, fontWeight: 600 }}>{user.name}</span>
              <span className="badge badge-navy" style={{ fontSize: 10, textTransform: 'capitalize' }}>{user.role}</span>
            </div>
            <button className="btn btn-ghost btn-sm" onClick={logout}>Logout</button>
          </div>
        </div>
        {PAGE_MAP[safePage]}
      </div>
    </div>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <Toaster position="top-right" />
      <CRMApp />
    </AuthProvider>
  );
}
